import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { logger } from '../utils/logger';
import { trackInteraction as trackGA } from './googleAnalytics';
import { debounce } from 'lodash';

const CONSENT_COOKIE = 'analytics_consent';
const SESSION_COOKIE = 'visitor_session';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export function hasAnalyticsConsent(): boolean | undefined {
  const consent = Cookies.get(CONSENT_COOKIE);
  return consent ? consent === 'true' : undefined;
}

export function setAnalyticsConsent(consent: boolean): void {
  if (consent) {
    Cookies.set(CONSENT_COOKIE, 'true', { 
      expires: 365, // 1 year
      sameSite: 'Lax',
      secure: window.location.protocol === 'https:'
    });
    
    // Update Google Consent Mode
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted'
      });
    }
    
    // Track initial pageview after consent
    trackPageView();
  } else {
    Cookies.remove(CONSENT_COOKIE);
    Cookies.remove(SESSION_COOKIE);
    
    // Update Google Consent Mode to deny
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'functionality_storage': 'denied',
        'personalization_storage': 'denied'
      });
    }
  }
}

export const trackPageView = debounce(async () => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  try {
    let sessionId = Cookies.get(SESSION_COOKIE);
    
    if (!sessionId) {
      sessionId = uuidv4();
      Cookies.set(SESSION_COOKIE, sessionId, { 
        expires: new Date(Date.now() + SESSION_DURATION),
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });
      
      await supabase
        .from('visitor_sessions')
        .insert([{ session_id: sessionId }]);
    } else {
      // Update last_seen timestamp
      await supabase
        .from('visitor_sessions')
        .update({ last_seen: new Date().toISOString() })
        .eq('session_id', sessionId);
    }

    // Track pageview in database
    await supabase
      .from('page_views')
      .insert([{
        page: window.location.pathname,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        ip_hash: null // We don't track IP addresses for privacy
      }]);

    // Track with Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        custom_map: {
          dimension1: sessionId
        }
      });
    }

  } catch (error) {
    logger.error('Error tracking page view:', error);
  }
}, 1000);

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean) => {
  if (!hasAnalyticsConsent()) return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'form_submit', {
      form_name: formName,
      success: success,
      event_category: 'engagement',
      event_label: formName
    });
  }
};

// Track user interactions
export const trackInteraction = (category: string, action: string, label?: string) => {
  if (!hasAnalyticsConsent()) return;

  // Track with Google Analytics
  trackGA(category, action, label);
  
  // Track in our database for detailed analytics
  supabase
    .from('user_interactions')
    .insert([{
      category,
      action,
      label,
      page: window.location.pathname,
      timestamp: new Date().toISOString()
    }])
    .catch(error => logger.error('Error tracking interaction:', error));
};

// Track search queries
export const trackSearch = (query: string, results: number) => {
  if (!hasAnalyticsConsent()) return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'search', {
      search_term: query,
      search_results: results
    });
  }
  
  supabase
    .from('search_queries')
    .insert([{
      query,
      results_count: results,
      page: window.location.pathname,
      timestamp: new Date().toISOString()
    }])
    .catch(error => logger.error('Error tracking search:', error));
};

// Track conversion events
export const trackConversion = (type: string, value?: number) => {
  if (!hasAnalyticsConsent()) return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      event_category: 'Conversion',
      event_label: type,
      value: value
    });
  }
  
  supabase
    .from('conversions')
    .insert([{
      type,
      value,
      page: window.location.pathname,
      timestamp: new Date().toISOString()
    }])
    .catch(error => logger.error('Error tracking conversion:', error));
}